import React, { useState, useEffect } from 'react';
import { Form, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { DetailedProduct } from '../../../../api/shop/types/productTypes';
import { getSellerLocations } from '../../../../api/shop/sellers/sellerLocationService';
import { getSellers } from '../../../../api/shop/sellers/sellerService';
import { SellerLocation, Seller } from '../../../../api/shop/types/sellerTypes';
import { LocationBadge } from '../../../../Common/BadgeStyles';
import './ProductSellerLocations.css';

interface ProductSellerLocationsProps {
  data: Partial<DetailedProduct>;
  onChange: (updates: Partial<DetailedProduct>) => void;
  onFieldTouch?: (fieldName: string) => void;
  isReadOnly?: boolean;
  showValidation?: boolean;
  touchedFields?: string[];
}

const ProductSellerLocations: React.FC<ProductSellerLocationsProps> = ({ 
  data, 
  onChange, 
  onFieldTouch,
  isReadOnly = false,
  showValidation = false,
  touchedFields = []
}) => {
  const [availableSellers, setAvailableSellers] = useState<Seller[]>([]);
  const [availableLocations, setAvailableLocations] = useState<SellerLocation[]>([]);
  const [sellersLoading, setSellersLoading] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [sellersError, setSellersError] = useState<string | null>(null);
  const [locationsError, setLocationsError] = useState<string | null>(null);

  // Fetch all available sellers
  useEffect(() => {
    const fetchSellers = async () => {
      try {
        setSellersLoading(true);
        setSellersError(null);
        
        const response = await getSellers({ 
          pageNum: 1,
          status: 'ACTIVE' 
        });
        
        setAvailableSellers(response.paginatedResults || []);
        
      } catch (error) {
        setSellersError('Failed to load sellers. Please try again.');
        setAvailableSellers([]);
      } finally {
        setSellersLoading(false);
      }
    };

    fetchSellers();
  }, []);

  // Fetch locations for the selected seller
  useEffect(() => {
    const fetchLocationsForSeller = async () => {
      // Only fetch if we have a seller selected
      if (!data.seller) {
        setAvailableLocations([]);
        setLocationsLoading(false);
        setLocationsError(null);
        return;
      }

      try {
        setLocationsLoading(true);
        setLocationsError(null);
        
        // Fetch locations filtered by seller
        const response = await getSellerLocations({ 
          seller: data.seller,
          status: 'ACTIVE' // Only active locations
        });
        
        // Ensure each location has the required properties
        const formattedLocations = (response.paginatedResults || []).map((location: any) => ({
          ...location,
          _id: location._id,
          seller: location.seller || '',
          locationCode: location.locationCode || '',
          type: location.type || 'ACADEMY',
          name: location.name || '',
          address: location.address || {
            line1: '',
            city: '',
            state: '',
            pincode: ''
          },
          fulfillmentCapabilities: location.fulfillmentCapabilities || ['PICKUP'],
          status: location.status || 'ACTIVE'
        }));
        
        setAvailableLocations(formattedLocations);
        
      } catch (error) {
        setLocationsError('Failed to load locations for the selected seller. Please try again.');
        setAvailableLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };

    fetchLocationsForSeller();
  }, [data.seller]); // Depend on seller selection

  const handleSellerChange = (selectedOption: any) => {
    if (isReadOnly) return;
    
    const sellerId = selectedOption?.value || '';
    
    // Clear seller locations when seller changes
    onChange({ 
      seller: sellerId,
      sellerLocations: [] // Reset locations when seller changes
    });
    
    if (onFieldTouch) {
      onFieldTouch('seller');
    }
  };

  const handleLocationToggle = (locationId: string) => {
    if (isReadOnly) return;

    const currentLocations = data.sellerLocations || [];
    const updatedLocations = currentLocations.includes(locationId)
      ? currentLocations.filter(id => id !== locationId)
      : [...currentLocations, locationId];
    
    onChange({ sellerLocations: updatedLocations });
  };

  // New handlers for dropdown interface
  const handleLocationAdd = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (isReadOnly) return;

    const locationId = e.target.value;
    if (!locationId) return;

    const currentSelections = data.sellerLocations || [];
    if (!currentSelections.includes(locationId)) {
      const updatedSelections = [...currentSelections, locationId];
      onChange({ sellerLocations: updatedSelections });
    }

    if (onFieldTouch) {
      onFieldTouch('sellerLocations');
    }

    // Reset the select to default
    e.target.value = '';
  };

  const handleRemoveLocation = (locationId: string | undefined) => {
    if (isReadOnly || !locationId) return;

    const currentSelections = data.sellerLocations || [];
    const updatedSelections = currentSelections.filter(id => id !== locationId);
    onChange({ sellerLocations: updatedSelections });
  };

  // Validation state
  const isSellerFieldTouched = touchedFields.includes('seller');
  const hasSellerError = showValidation && isSellerFieldTouched && !data.seller;
  
  const isLocationFieldTouched = touchedFields.includes('sellerLocations');
  const hasLocationValidationError = showValidation && isLocationFieldTouched && (!data.sellerLocations || data.sellerLocations.length === 0);
  
  // Find selected seller for display
  const selectedSeller = availableSellers.find(seller => seller._id === data.seller || seller.id === data.seller);

  // Show initial loading for sellers
  const isExternalAds = data.fulfillmentCapabilities?.includes('EXTERNAL_ADS');

  if (sellersLoading) {
    return (
      <div>
        <h5 className="card-title mb-3">
          {isExternalAds ? 'Seller & Ads' : 'Seller & Locations'}
          <span className="text-danger"> *</span>
        </h5>
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading sellers...</span>
        </div>
      </div>
    );
  }

  // Get selected location names for badge display
  const selectedLocationNames = data.sellerLocations 
    ? availableLocations
        .filter(location => data.sellerLocations?.includes(location._id) && location.name) // Only locations with names
        .map(location => location.name!) // Safe to use ! since we filtered for non-null names
    : [];

  return (
    <div>
      <h5 className="card-title mb-3">
        {isExternalAds ? 'Seller & Ads' : 'Seller & Locations'}
        <span className="text-danger"> *</span>
      </h5>

      {/* Seller Selection */}
      <div className="mb-4">
        <Form.Group className="mb-3">
          <Form.Label className="required-label">
            Select Seller <span className="text-danger">*</span>
          </Form.Label>
          
          {/* Show seller error if any */}
          {sellersError && (
            <Alert variant="danger" className="mb-3">
              <i className="ri-error-warning-line me-2"></i>
              {sellersError}
            </Alert>
          )}
          
          {availableSellers.length === 0 && !sellersLoading ? (
            <div className="text-center py-4 text-muted">
              <i className="ri-store-2-line fs-1"></i>
              <p className="mt-2">No active sellers available</p>
              {sellersError && (
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => window.location.reload()}
                >
                  <i className="ri-refresh-line me-1"></i>
                  Retry
                </button>
              )}
            </div>
          ) : (
            <Select
              classNamePrefix="react-select"
              value={selectedSeller ? {
                value: selectedSeller._id || selectedSeller.id,
                label: selectedSeller.name
              } : null}
              onChange={handleSellerChange}
              options={availableSellers.map(seller => ({
                value: seller._id || seller.id,
                label: seller.name
              }))}
              placeholder="Choose a seller..."
              isSearchable={true}
              isDisabled={isReadOnly || sellersLoading}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base, state) => ({
                  ...base,
                  minHeight: '44px',
                  borderColor: hasSellerError ? '#dc3545' : (state.isFocused ? '#4e73df' : '#ced4da'),
                  boxShadow: state.isFocused 
                    ? hasSellerError 
                      ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
                      : '0 0 0 0.2rem rgba(78, 115, 223, 0.25)'
                    : 'none'
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#6c757d'
                })
              }}
              className="seller-select-react"
            />
          )}
          
          {hasSellerError && (
            <div className="invalid-feedback d-block">
              Please select a seller
            </div>
          )}
        </Form.Group>
      </div>

      {/* Locations Section or Ad ID Section */}
      {isExternalAds ? (
        <div className="mb-4">
          <Form.Group>
            <Form.Label>
              Ad ID <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={data.adsId || ''}
              onChange={(e) => onChange({ adsId: e.target.value })}
              placeholder="Enter Ad ID"
              disabled={isReadOnly}
            />
          </Form.Group>
        </div>
      ) : (
        <div className="mb-4">
          <h6 className="mb-3">Academy Locations <span className="text-danger">*</span></h6>

          {/* Show locations error if any */}
          {locationsError && (
            <Alert variant="danger" className="mb-3">
              <i className="ri-error-warning-line me-2"></i>
              {locationsError}
            </Alert>
          )}

          {/* Show message when no seller is selected */}
          {!data.seller && (
            <Alert variant="info" className="mb-3">
              <i className="ri-information-line me-2"></i>
              Please select a seller first to view available locations.
            </Alert>
          )}

          {/* Show locations loading */}
          {locationsLoading && data.seller && (
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="ms-2">Loading locations...</span>
            </div>
          )}

          {/* Show badges only in read-only mode */}
          {isReadOnly && selectedLocationNames.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {selectedLocationNames.map((locationName, index) => (
                <LocationBadge key={index} locationName={locationName} />
              ))}
            </div>
          )}

          {/* Show dropdown interface in edit mode when seller is selected */}
          {!isReadOnly && data.seller && !locationsLoading && (
            <>
              {/* Selected Locations as Badges */}
              {selectedLocationNames.length > 0 && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">Selected locations:</small>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedLocationNames.map((locationName, index) => {
                      const locationId = data.sellerLocations?.[index];
                      return (
                        <div key={index} className="selected-location-badge">
                          <LocationBadge locationName={locationName} />
                          <button
                            type="button"
                            className="location-badge-remove"
                            onClick={() => handleRemoveLocation(locationId)}
                            title="Remove location"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Location Dropdown */}
              {availableLocations.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="ri-map-pin-line fs-1"></i>
                  <p className="mt-2">No active locations available for the selected seller</p>
                  {locationsError && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => window.location.reload()}
                    >
                      <i className="ri-refresh-line me-1"></i>
                      Retry
                    </button>
                  )}
                </div>
              ) : (
                <div className="mb-3">
                  <Form.Label className="required-label">
                    Choose locations where this product will be available
                  </Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={null}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        handleLocationAdd({ target: { value: selectedOption.value } } as any);
                      }
                    }}
                    options={availableLocations
                      .filter(location => !data.sellerLocations?.includes(location._id))
                      .map(location => ({
                        value: location._id,
                        label: `${location.name} - ${location.type === 'ACADEMY' ? 'Academy' : 'Warehouse'} (${location.address.line1}, ${location.address.city})`
                      }))}
                    placeholder="Choose a location to add..."
                    isSearchable={true}
                    isDisabled={locationsLoading}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (base, state) => ({
                        ...base,
                        minHeight: '44px',
                        borderColor: hasLocationValidationError ? '#dc3545' : (state.isFocused ? '#4e73df' : '#ced4da'),
                        boxShadow: state.isFocused
                          ? hasLocationValidationError
                            ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
                            : '0 0 0 0.2rem rgba(78, 115, 223, 0.25)'
                          : 'none'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#6c757d'
                      })
                    }}
                    className="location-select-react"
                  />
                </div>
              )}

              {/* Validation feedback */}
              {hasLocationValidationError && (
                <div className="invalid-feedback d-block">
                  Please select at least one location
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSellerLocations;